import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Box, Fab, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useAgentStore from "../useAgentStore";
import useSchedulingStore from "../useSchedulingStore";
import useChatStreamStore from "../useChatStreamStore";
import { useStatusPalette } from "../../../utils/statusColors";
import {
  FORM_PANEL_DEFAULT_WIDTH,
  FORM_PANEL_MIN_WIDTH,
  FORM_PANEL_MAX_WIDTH,
  CHAT_MIN_WIDTH,
  SPLIT_RESIZE_HANDLE_WIDTH,
  STATUS_WAITING_FOR_REVIEW,
  STATUS_SESSION_UNDER_REVIEW,
  STATUS_HUMAN_TAKEOVER,
  DEFAULT_BLOCKED_WHILE_REVIEW_MESSAGE,
} from "./conversationConstants";
import {
  normalizeChatUiPolicy,
  getLatestDynamicFormEvent,
  getLatestCreatedFileEvent,
  computeConversationScrollRevision,
  isAgentBusyStatus,
} from "./conversationUtils";
import ChatHeader from "./ChatHeader";
import {
  ThinkingAgentsPanel,
  ExecutionFallbackIndicator,
  ConnectionStatusBanner,
  SendErrorBanner,
  SessionErrorBanner,
  WaitingForWorkersBanner,
  deriveToolLabelFromParts,
} from "./ActivityIndicators";
import MessageComposer from "./MessageComposer";
import { buildConversationMessages } from "./buildConversationMessages";
import useWorkerStatuses from "./useWorkerStatuses";
import ManagedRunOperatorBanner from "./ManagedRunOperatorBanner";
import ConversationRightPanel from "./ConversationRightPanel";
import useConversationRightPanel from "./useConversationRightPanel";

const EMPTY_MESSAGES = [];

function AgentConversationView({
  session,
  agentId,
  agentName,
  agentModel,
  agentInboxAddress,
  chatUiPolicy,
  onOpenAgentSettings,
  onSendMessage,
  onScheduleMessage,
  onStopSession,
  chatStatusOverride,
  allowScheduling = true,
  allowStop = true,
  composerDisabled = false,
  operatorView = true,
  hideHeader = false,
  leadingActions = null,
  headerActions = null,
  scheduledMessages = null,
  onEditScheduledMessage = null,
  onCancelScheduledMessage = null,
  onRefreshScheduledMessages = null,
  activityPanel = null,
  thinkingAgentsOverride = null,
  messagesOverride = null,
  showExecutionFallbackLoader = false,
  executionFallbackLabel = "Processing",
  debugModeEnabled = false,
  agents,
  // --- task queue props ---
  showTaskQueueToggle = false,
  taskQueuePanelOpen = false,
  taskQueuePanelProps = null,
  taskQueuePanelWidth = 400,
  taskQueueExpandedMode = false,
  onOpenTaskQueuePanel,
  onCloseTaskQueuePanel,
  onTaskQueueResizeStart,
  splitChatFixedWidth = null,
}) {
  const _sp = useStatusPalette();
  const splitContainerRef = useRef(null);
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const isNearBottomRef = useRef(true);
  const sessionMessages =
    useAgentStore((s) => s.sessionDetail?.messages) ?? EMPTY_MESSAGES;
  const messages = messagesOverride || sessionMessages;
  const { bySessionId: workerStatusMap } = useWorkerStatuses(
    session?.chatSessionId
  );
  const activeBlockingWorkerCount = Object.values(workerStatusMap).filter(
    (w) => w.status === "IN_PROGRESS"
  ).length;
  const messageCount = messages.length;
  const scrollRevision = computeConversationScrollRevision(messages);
  const chatStatus = useAgentStore((state) => state.chatStatus);
  const sendError = useAgentStore((state) => state.sendError);
  const streamError = useChatStreamStore((state) => state.streamError);
  const connectionState = useChatStreamStore((state) =>
    session?.chatSessionId
      ? state.sessionStreamStateBySessionId[session.chatSessionId] || "idle"
      : "idle"
  );
  const effectiveChatStatus = chatStatusOverride || chatStatus;
  const effectiveThinkingAgents = Array.isArray(thinkingAgentsOverride)
    ? thinkingAgentsOverride
    : null;
  const [humanTakeoverBusy, setHumanTakeoverBusy] = useState(false);
  const [activeRightTab, setActiveRightTab] = useState(null); // "form" | "file" | "taskQueue" | null
  const [activeFormContext, setActiveFormContext] = useState(null);
  const [activeFileContext, setActiveFileContext] = useState(null);
  const [isFormFullscreen, setIsFormFullscreen] = useState(false);
  const [isFileFullscreen, setIsFileFullscreen] = useState(false);
  const [formRefreshTrigger, setFormRefreshTrigger] = useState(0);
  const [fileRefreshTrigger, setFileRefreshTrigger] = useState(0);
  const [splitContainerWidth, setSplitContainerWidth] = useState(null);
  const [formPanelWidth, setFormPanelWidth] = useState(
    FORM_PANEL_DEFAULT_WIDTH
  );
  const [filePanelWidth, setFilePanelWidth] = useState(
    FORM_PANEL_DEFAULT_WIDTH
  );
  const isResizingFormPanelRef = useRef(false);
  const isResizingFilePanelRef = useRef(false);
  const lastObservedDynamicFormEventKeyRef = useRef(null);
  const lastObservedCreatedFileEventKeyRef = useRef(null);
  const autoOpenDynamicFormInitializedForSessionRef = useRef(false);
  const autoOpenCreatedFileInitializedForSessionRef = useRef(false);
  const latestDynamicFormEvent = useMemo(
    () => getLatestDynamicFormEvent(messages),
    [messages]
  );
  const latestCreatedFileEvent = useMemo(
    () => getLatestCreatedFileEvent(messages),
    [messages]
  );

  const isUnderReview =
    session?.status === STATUS_WAITING_FOR_REVIEW ||
    session?.status === STATUS_SESSION_UNDER_REVIEW;
  const underReviewMessage =
    typeof session?.blockedWhileReviewMessage === "string" &&
    session.blockedWhileReviewMessage.trim()
      ? session.blockedWhileReviewMessage.trim()
      : DEFAULT_BLOCKED_WHILE_REVIEW_MESSAGE;
  const sessionStatusReason =
    typeof session?.statusReason === "string" && session.statusReason.trim()
      ? session.statusReason.trim()
      : "";
  const showDebugErrorReason = Boolean(
    debugModeEnabled && session?.status === "ERROR" && sessionStatusReason
  );
  const isManagedRunChat =
    session?.managedRun === true ||
    session?.source === "WORKFLOW_OPERATOR_MANAGED_RUN";
  const isActiveManagedRun =
    session?.managedRun === true && session?.managedRunStatus !== "STOPPED";
  const isHumanTakeover = session?.status === STATUS_HUMAN_TAKEOVER;
  const isWaitingForWorkers = session?.status === "WAITING_FOR_WORKERS";
  const composerLocked = Boolean(
    composerDisabled || isUnderReview || isWaitingForWorkers
  );
  const isThinking =
    !isHumanTakeover && isAgentBusyStatus(effectiveChatStatus);
  const hasThinkingAgents =
    Array.isArray(effectiveThinkingAgents) &&
    effectiveThinkingAgents.length > 0;
  const streamToolLabel = useMemo(() => {
    const streamingMsg = messages.find((m) => m.status === "streaming");
    return streamingMsg ? deriveToolLabelFromParts([streamingMsg]) : undefined;
  }, [messages]);
  const {
    availableRightTabs,
    rightPanelOpen,
    showRightPanelTabBar,
    isRightPanelFullscreen,
    rightPanelWidth,
    shouldStackRightPanel,
  } = useConversationRightPanel({
    activeFormContext,
    activeFileContext,
    showTaskQueueToggle,
    taskQueuePanelOpen,
    activeRightTab,
    isFormFullscreen,
    isFileFullscreen,
    taskQueueExpandedMode,
    taskQueuePanelWidth,
    filePanelWidth,
    formPanelWidth,
    splitContainerWidth,
  });

  // Embedded panels (e.g. the canvas operator chat) pin the chat column to a
  // fixed width so all extra panel width flows to the right panel instead of
  // widening the chat. Only applies in the side-by-side (non-stacked,
  // non-fullscreen) layout.
  const useFixedChatSplit =
    typeof splitChatFixedWidth === "number" &&
    splitChatFixedWidth > 0 &&
    rightPanelOpen &&
    !isRightPanelFullscreen &&
    !shouldStackRightPanel;

  const handleOpenDynamicForm = useCallback((formContext) => {
    if (!formContext?.formId) return;
    setActiveFormContext(formContext);
    setActiveRightTab("form");
  }, []);

  const handleOpenCreatedFile = useCallback((fileContext) => {
    if (!fileContext?.fileId && !fileContext?.downloadUrl) return;
    setActiveFileContext(fileContext);
    setActiveRightTab("file");
  }, []);

  const handleCloseDynamicForm = useCallback(() => {
    setActiveFormContext(null);
    setIsFormFullscreen(false);
    setActiveRightTab((prev) => (prev === "form" ? null : prev));
  }, []);

  const handleCloseCreatedFile = useCallback(() => {
    setActiveFileContext(null);
    setIsFileFullscreen(false);
    setActiveRightTab((prev) => (prev === "file" ? null : prev));
  }, []);

  const handleRefreshDynamicForm = useCallback(() => {
    setFormRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleRefreshCreatedFile = useCallback(() => {
    setFileRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleToggleCurrentFullscreen = useCallback(() => {
    if (activeRightTab === "form") setIsFormFullscreen((prev) => !prev);
    else if (activeRightTab === "file") setIsFileFullscreen((prev) => !prev);
    else if (
      activeRightTab === "taskQueue" &&
      taskQueuePanelProps?.onExpandedModeChange
    ) {
      taskQueuePanelProps.onExpandedModeChange(!taskQueueExpandedMode);
    }
  }, [activeRightTab, taskQueueExpandedMode, taskQueuePanelProps]);

  const clampFormPanelWidth = useCallback((candidateWidth) => {
    const containerRect = splitContainerRef.current?.getBoundingClientRect();
    if (!containerRect) return candidateWidth;
    const isCompact = containerRect.width < 920;
    const minChatWidth = isCompact
      ? Math.max(180, Math.floor(containerRect.width * 0.42))
      : CHAT_MIN_WIDTH;
    const minPanelWidth = isCompact
      ? Math.max(180, Math.floor(containerRect.width * 0.35))
      : FORM_PANEL_MIN_WIDTH;
    const maxPanelWidth = Math.max(
      minPanelWidth,
      Math.min(
        FORM_PANEL_MAX_WIDTH,
        containerRect.width - minChatWidth - SPLIT_RESIZE_HANDLE_WIDTH
      )
    );
    return Math.max(minPanelWidth, Math.min(maxPanelWidth, candidateWidth));
  }, []);

  const handleFormPanelResizeStart = useCallback((event) => {
    event.preventDefault();
    isResizingFormPanelRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  const handleFilePanelResizeStart = useCallback((event) => {
    event.preventDefault();
    isResizingFilePanelRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  const handleActiveTabResizeStart = useCallback(
    (event) => {
      if (activeRightTab === "taskQueue" && onTaskQueueResizeStart) {
        onTaskQueueResizeStart(event);
      } else if (activeRightTab === "file") {
        handleFilePanelResizeStart(event);
      } else {
        handleFormPanelResizeStart(event);
      }
    },
    [
      activeRightTab,
      onTaskQueueResizeStart,
      handleFilePanelResizeStart,
      handleFormPanelResizeStart,
    ]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const handleScroll = () => {
      const threshold = 120;
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      isNearBottomRef.current = nearBottom;
      setIsNearBottom(nearBottom);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const isFollowingStream =
    effectiveChatStatus === "streaming" || effectiveChatStatus === "submitted";

  const stickToBottom = useCallback(
    (behavior = "auto") => {
      if (!isNearBottomRef.current) return;
      const el = scrollRef.current;
      if (!el) return;
      if (behavior === "smooth" && bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: "smooth" });
        return;
      }
      el.scrollTop = el.scrollHeight;
    },
    []
  );

  useLayoutEffect(() => {
    stickToBottom(isFollowingStream ? "auto" : "smooth");
  }, [messageCount, effectiveChatStatus, scrollRevision, isFollowingStream, stickToBottom]);

  const scrollToBottom = useCallback(() => {
    isNearBottomRef.current = true;
    setIsNearBottom(true);
    stickToBottom("smooth");
  }, [stickToBottom]);

  useEffect(() => {
    if (allowScheduling && agentId) {
      useSchedulingStore.getState().fetchScheduledMessagesByAgent(agentId);
    }
  }, [agentId, allowScheduling]);

  useEffect(() => {
    const node = splitContainerRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      const nextWidth = node.getBoundingClientRect().width;
      setSplitContainerWidth((prev) =>
        prev !== null && Math.abs(prev - nextWidth) < 1 ? prev : nextWidth
      );
    };

    updateWidth();

    if (typeof ResizeObserver === "function") {
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(node);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!isResizingFormPanelRef.current && !isResizingFilePanelRef.current) {
        return;
      }
      const containerRect = splitContainerRef.current?.getBoundingClientRect();
      if (!containerRect) return;
      const nextWidth = containerRect.right - event.clientX;
      if (isResizingFormPanelRef.current) {
        setFormPanelWidth(clampFormPanelWidth(nextWidth));
      }
      if (isResizingFilePanelRef.current) {
        setFilePanelWidth(clampFormPanelWidth(nextWidth));
      }
    };

    const stopResize = () => {
      if (!isResizingFormPanelRef.current && !isResizingFilePanelRef.current) {
        return;
      }
      isResizingFormPanelRef.current = false;
      isResizingFilePanelRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopResize);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [clampFormPanelWidth]);

  useEffect(() => {
    setActiveFormContext(null);
    setActiveFileContext(null);
    setIsFormFullscreen(false);
    setIsFileFullscreen(false);
    setFormRefreshTrigger(0);
    setFileRefreshTrigger(0);
    setFormPanelWidth(FORM_PANEL_DEFAULT_WIDTH);
    setFilePanelWidth(FORM_PANEL_DEFAULT_WIDTH);
    setActiveRightTab(null);
    lastObservedDynamicFormEventKeyRef.current = null;
    lastObservedCreatedFileEventKeyRef.current = null;
    autoOpenDynamicFormInitializedForSessionRef.current = false;
    autoOpenCreatedFileInitializedForSessionRef.current = false;
  }, [session?.chatSessionId]);

  // Resolve active tab when a panel is closed
  useEffect(() => {
    const available = [];
    if (activeFormContext) available.push("form");
    if (activeFileContext) available.push("file");
    if (showTaskQueueToggle && taskQueuePanelOpen) available.push("taskQueue");
    if (activeRightTab && !available.includes(activeRightTab)) {
      setActiveRightTab(available[0] || null);
    }
  }, [
    activeFormContext,
    activeFileContext,
    showTaskQueueToggle,
    taskQueuePanelOpen,
    activeRightTab,
  ]);

  // Auto-select taskQueue tab when TQ panel opens and no tab is active
  useEffect(() => {
    if (showTaskQueueToggle && taskQueuePanelOpen) {
      setActiveRightTab((prev) => prev || "taskQueue");
    }
  }, [showTaskQueueToggle, taskQueuePanelOpen]);

  // Clamp panel widths
  useEffect(() => {
    if (!activeFormContext || shouldStackRightPanel || isRightPanelFullscreen)
      return;
    setFormPanelWidth((prev) => clampFormPanelWidth(prev));
  }, [
    activeFormContext,
    clampFormPanelWidth,
    shouldStackRightPanel,
    isRightPanelFullscreen,
  ]);

  useEffect(() => {
    if (!activeFileContext || shouldStackRightPanel || isRightPanelFullscreen)
      return;
    setFilePanelWidth((prev) => clampFormPanelWidth(prev));
  }, [
    activeFileContext,
    clampFormPanelWidth,
    shouldStackRightPanel,
    isRightPanelFullscreen,
  ]);

  useEffect(() => {
    if (!autoOpenDynamicFormInitializedForSessionRef.current) {
      lastObservedDynamicFormEventKeyRef.current =
        latestDynamicFormEvent?.key || null;
      autoOpenDynamicFormInitializedForSessionRef.current = true;
      return;
    }

    if (!latestDynamicFormEvent?.key) return;
    lastObservedDynamicFormEventKeyRef.current = latestDynamicFormEvent.key;
  }, [latestDynamicFormEvent]);

  useEffect(() => {
    if (!autoOpenCreatedFileInitializedForSessionRef.current) {
      lastObservedCreatedFileEventKeyRef.current =
        latestCreatedFileEvent?.key || null;
      autoOpenCreatedFileInitializedForSessionRef.current = true;
      return;
    }

    if (!latestCreatedFileEvent?.key) return;
    lastObservedCreatedFileEventKeyRef.current = latestCreatedFileEvent.key;
  }, [latestCreatedFileEvent]);

  const handleSend = (text, imageUrls) => {
    if (typeof onSendMessage === "function") {
      onSendMessage(text, imageUrls);
      return;
    }
    useAgentStore
      .getState()
      .sendMessage(session.chatSessionId, agentId, text, imageUrls);
  };

  const handleSchedule = (text, date) => {
    if (typeof onScheduleMessage === "function") {
      onScheduleMessage(text, date);
      return;
    }
    if (!allowScheduling) return;
    const resumeAt = date.toISOString();
    useSchedulingStore
      .getState()
      .scheduleUserMessage(session.chatSessionId, agentId, text, resumeAt);
  };

  const handleStop = () => {
    if (typeof onStopSession === "function") {
      onStopSession();
      return;
    }
    useAgentStore.getState().cancelSession(session.chatSessionId);
  };

  const handleStartHumanTakeover = async () => {
    if (humanTakeoverBusy) return;
    setHumanTakeoverBusy(true);
    try {
      await useAgentStore.getState().startHumanTakeover(session.chatSessionId);
    } finally {
      setHumanTakeoverBusy(false);
    }
  };

  const handleEndHumanTakeover = async () => {
    if (humanTakeoverBusy) return;
    setHumanTakeoverBusy(true);
    try {
      await useAgentStore.getState().endHumanTakeover(session.chatSessionId);
    } finally {
      setHumanTakeoverBusy(false);
    }
  };

  if (!session) return null;
  const effectiveChatUiPolicy = normalizeChatUiPolicy(chatUiPolicy);
  const allowHumanTakeoverControl =
    operatorView && effectiveChatUiPolicy.enableHumanTakeoverMode;
  const { rendered, lastRole, isShowingThinking } = buildConversationMessages({
    messages,
    session,
    defaultAgentId: agentId,
    defaultAgentName: agentName,
    chatUiPolicy: effectiveChatUiPolicy,
    scheduledMessages,
    onEditScheduledMessage,
    onCancelScheduledMessage,
    onRefreshScheduledMessages,
    onOpenDynamicForm: handleOpenDynamicForm,
    onOpenCreatedFile: handleOpenCreatedFile,
    onOpenAgentSettings,
    chatStatus: isHumanTakeover ? "ready" : effectiveChatStatus,
    streamToolLabel,
    workerStatusMap,
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        width: "100%",
        height: "100%",
        overflowX: "hidden",
      }}
    >
      {!hideHeader && (
        <ChatHeader
          session={session}
          agentInboxAddress={agentInboxAddress}
          showSessionOperationalMeta={
            effectiveChatUiPolicy.showSessionOperationalMetaInChat
          }
          operatorView={operatorView}
          allowHumanTakeoverControl={allowHumanTakeoverControl}
          humanTakeoverBusy={humanTakeoverBusy}
          onStartHumanTakeover={handleStartHumanTakeover}
          onEndHumanTakeover={handleEndHumanTakeover}
          leadingActions={leadingActions}
          actions={headerActions}
        />
      )}
      {isManagedRunChat && (
        <ManagedRunOperatorBanner
          session={session}
          isActive={isActiveManagedRun}
        />
      )}
      {showDebugErrorReason && (
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            borderBottom: 1,
            borderColor: _sp.red.border,
            backgroundColor: _sp.red.bg,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: "12px", color: _sp.red.color }}>
            Debug error detail: {sessionStatusReason}
          </Typography>
        </Box>
      )}
      {isUnderReview && (
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            borderBottom: 1,
            borderColor: _sp.amberAlt.border,
            backgroundColor: _sp.amberAlt.bg,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: "12px", color: _sp.amberAlt.color }}>
            {underReviewMessage}
          </Typography>
        </Box>
      )}
      <Box
        ref={splitContainerRef}
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection:
            isRightPanelFullscreen || shouldStackRightPanel ? "column" : "row",
          overflowY: "hidden",
          overflowX: "hidden",
        }}
      >
        {!isRightPanelFullscreen && (
          <Box
            sx={{
              ...(useFixedChatSplit
                ? {
                    flexGrow: 0,
                    flexShrink: 0,
                    width: `${splitChatFixedWidth}px`,
                    minWidth: `${splitChatFixedWidth}px`,
                  }
                : { flex: 1, minWidth: 0 }),
              display: "flex",
              flexDirection: "column",
            }}
          >
            {activityPanel}
            <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
              <Box
                ref={scrollRef}
                sx={{
                  height: "100%",
                  overflowY: "auto",
                  overflowX: "auto",
                  scrollbarWidth: "thin",
                  scrollbarColor: (theme) =>
                    `${theme.palette.divider} transparent`,
                  py: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                }}
              >
                {rendered}
                <SessionErrorBanner
                  status={session?.status}
                  statusReason={session?.statusReason}
                />
                <WaitingForWorkersBanner
                  status={session?.status}
                  workerCount={activeBlockingWorkerCount}
                />
                <SendErrorBanner
                  error={sendError}
                  onDismiss={() => useAgentStore.getState().clearError()}
                />
                <ConnectionStatusBanner
                  connectionState={connectionState}
                  streamError={streamError}
                />
                {hasThinkingAgents && !isShowingThinking && (
                  <ThinkingAgentsPanel
                    thinkingAgents={effectiveThinkingAgents}
                    onOpenAgentSettings={onOpenAgentSettings}
                  />
                )}
                {showExecutionFallbackLoader && (
                  <ExecutionFallbackIndicator label={executionFallbackLabel} />
                )}
                <div ref={bottomRef} />
              </Box>
              {!isNearBottom && (
                <Fab
                  size="small"
                  onClick={scrollToBottom}
                  sx={{
                    position: "absolute",
                    bottom: 12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 32,
                    height: 32,
                    minHeight: 0,
                    backgroundColor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    boxShadow: 2,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                </Fab>
              )}
            </Box>
            <MessageComposer
              onSend={handleSend}
              onSchedule={handleSchedule}
              onStop={handleStop}
              disabled={isThinking || composerLocked}
              isThinking={isThinking}
              isUnderReview={isUnderReview}
              isWaitingForWorkers={isWaitingForWorkers}
              underReviewMessage={underReviewMessage}
              allowScheduling={allowScheduling}
              allowStop={allowStop}
              agentId={agentId}
              agentModel={agentModel}
              agents={agents}
            />
          </Box>
        )}
        {rightPanelOpen && (
          <>
            {!isRightPanelFullscreen && !shouldStackRightPanel && !useFixedChatSplit && (
              <Box
                onMouseDown={handleActiveTabResizeStart}
                sx={{
                  width: `${SPLIT_RESIZE_HANDLE_WIDTH}px`,
                  cursor: "col-resize",
                  flexShrink: 0,
                  backgroundColor: "action.hover",
                  transition: "background-color 120ms ease",
                  "&:hover": { backgroundColor: "action.selected" },
                }}
              />
            )}
            <ConversationRightPanel
              isRightPanelFullscreen={isRightPanelFullscreen}
              shouldStackRightPanel={shouldStackRightPanel}
              rightPanelFlex={useFixedChatSplit}
              rightPanelWidth={rightPanelWidth}
              showRightPanelTabBar={showRightPanelTabBar}
              availableRightTabs={availableRightTabs}
              activeRightTab={activeRightTab}
              setActiveRightTab={setActiveRightTab}
              activeFormContext={activeFormContext}
              activeFileContext={activeFileContext}
              setActiveFileContext={setActiveFileContext}
              handleCloseDynamicForm={handleCloseDynamicForm}
              handleCloseCreatedFile={handleCloseCreatedFile}
              onCloseTaskQueuePanel={onCloseTaskQueuePanel}
              handleToggleCurrentFullscreen={handleToggleCurrentFullscreen}
              handleRefreshDynamicForm={handleRefreshDynamicForm}
              handleRefreshCreatedFile={handleRefreshCreatedFile}
              formRefreshTrigger={formRefreshTrigger}
              fileRefreshTrigger={fileRefreshTrigger}
              taskQueuePanelProps={taskQueuePanelProps}
            />
          </>
        )}
      </Box>
    </Box>
  );
}

export { buildConversationMessages };
export default AgentConversationView;
